import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { InPostLogo } from '@/components/inpost-logo';
import { Button } from '@/components/ui/button';
import { ClientCredentialsForm } from '@/pages/authorization/ClientCredentialsForm';
import { useInPostConnect } from '@/pages/authorization/useInPostConnect';

const CONNECTION_GUIDE_URL = '#todo';
const inlineLinkClasses = 'text-xs font-medium text-muted-foreground underline';

/** Setup wizard step 2 - Figma 347-2807 (disconnected) / 347-2924 (connected). */
export function ConnectInPostStep({ onContinue }: { onContinue: () => void }) {
  const { t } = useTranslation('setup');
  const navigate = useNavigate();
  const {
    isConnected,
    needsOrgSelection,
    isStatusLoading,
    isStatusError,
    isConnecting,
    isConnectingClientCredentials,
    isBusy,
    canUseOAuthLogin,
    connect,
    connectWithClientCredentials,
  } = useInPostConnect();

  return (
    <div className="flex flex-col gap-[17px]">
      <InPostLogo variant="white-bg" className="h-9 w-auto self-start" />
      <h1 className="text-4xl font-medium tracking-[-0.9px] text-black">{t('inpost.title')}</h1>
      <p className="max-w-[495px] text-base text-muted-foreground">{t('inpost.description')}</p>

      {/* items-start keeps the guide link aligned with the top of the client-credentials form. */}
      <div
        className={
          isConnected || canUseOAuthLogin ? 'flex items-center gap-6' : 'flex items-start gap-6'
        }
      >
        {isStatusError ? (
          <p className="text-sm text-destructive">{t('inpost.statusError')}</p>
        ) : isConnected && !needsOrgSelection ? (
          /* Figma 347-2937: outlined black chip with 20px check. */
          <span className="inline-flex h-9 items-center justify-center gap-2 rounded-[10px] border border-black px-4 py-2 text-sm font-medium leading-5 text-black">
            <Check className="size-5 shrink-0" aria-hidden />
            {t('inpost.connected')}
          </span>
        ) : isConnected && needsOrgSelection ? (
          <Button
            type="button"
            variant="outline"
            className="rounded-[10px] border-black text-black shadow-none hover:bg-accent"
            disabled={isBusy}
            onClick={() => navigate('/connect/inpost/select-organization')}
          >
            {t('inpost.chooseOrganization')}
          </Button>
        ) : canUseOAuthLogin ? (
          <Button
            type="button"
            className="w-[186px] rounded-[10px]"
            disabled={isBusy || isStatusLoading}
            onClick={connect}
          >
            {isStatusLoading
              ? t('inpost.statusLoading')
              : isConnecting
                ? t('inpost.connecting')
                : t('inpost.connect')}
          </Button>
        ) : (
          /* Non-PL markets cannot use the browser login - connect with the
             merchant-supplied client credential pair instead. */
          <ClientCredentialsForm
            onConnect={connectWithClientCredentials}
            isConnecting={isConnectingClientCredentials}
            disabled={isBusy || isStatusLoading}
          />
        )}
        <a
          href={CONNECTION_GUIDE_URL}
          target="_blank"
          rel="noreferrer"
          className={inlineLinkClasses}
        >
          {t('inpost.readGuide')}
        </a>
      </div>

      {isConnected && !needsOrgSelection ? (
        <div className="flex flex-col gap-3">
          {/* Figma 347-2962: grey confirmation banner. */}
          <div className="flex h-12 items-center rounded-[12px] bg-[#f3f3f3] px-3 text-base text-black">
            {t('inpost.allSet')}
          </div>
          <Button type="button" className="w-[141px] rounded-[10px]" onClick={onContinue}>
            {t('inpost.continue')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
